import React, { useState } from "react";
import { ExternalLink, ShieldCheck, Loader2 } from "lucide-react";

/**
 * Embeds a Microsoft Forms form via iframe.
 * The form lives on Microsoft's GDPR-compliant infrastructure (EU region) —
 * no data passes through the OFA Emergent backend.
 *
 * Props:
 *  - url        : Microsoft Forms response URL (we add &embed=true automatically)
 *  - title      : iframe title (accessibility)
 *  - height     : iframe height (default 1100)
 *  - lang       : "fr" | "en" for fallback copy
 *  - testId     : root data-testid
 */
export function MSFormEmbed({ url, title, height = 1100, lang = "fr", testId = "ms-form-embed" }) {
  const [loaded, setLoaded] = useState(false);

  if (!url) {
    return (
      <div
        className="rounded-2xl border border-dashed border-border bg-muted/20 p-8 text-center text-sm text-muted-foreground"
        data-testid={`${testId}-missing-url`}
      >
        {lang === "fr"
          ? "Le formulaire n'est pas encore configuré. Revenez bientôt."
          : "The form is not configured yet. Please check back soon."}
      </div>
    );
  }

  // Microsoft Forms: appending &embed=true hides their top header for a cleaner look.
  const embedUrl = url.includes("embed=") ? url : `${url}${url.includes("?") ? "&" : "?"}embed=true`;

  const copy = lang === "fr"
    ? {
        privacy: "Formulaire sécurisé Microsoft Forms — vos données restent sur l'infrastructure Microsoft (zone UE) conforme RGPD. Aucune information n'est stockée sur cette plateforme.",
        openInTab: "Ouvrir dans un nouvel onglet",
        loading: "Chargement du formulaire sécurisé…",
      }
    : {
        privacy: "Secure Microsoft Forms — your data stays on Microsoft's GDPR-compliant infrastructure (EU region). Nothing is stored on this platform.",
        openInTab: "Open in a new tab",
        loading: "Loading the secure form…",
      };

  return (
    <div className="space-y-4" data-testid={testId}>
      <div
        className="flex items-start gap-3 rounded-xl border border-accent/30 bg-accent/5 p-3"
        data-testid={`${testId}-privacy-banner`}
      >
        <ShieldCheck className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" aria-hidden="true" />
        <p className="text-xs text-muted-foreground leading-relaxed">{copy.privacy}</p>
      </div>

      <div className="relative rounded-2xl overflow-hidden border border-border bg-card">
        {!loaded && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-card z-10 pointer-events-none"
            data-testid={`${testId}-loader`}
          >
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              <span>{copy.loading}</span>
            </div>
          </div>
        )}
        <iframe
          src={embedUrl}
          title={title || "Microsoft Forms"}
          width="100%"
          height={height}
          frameBorder="0"
          marginHeight="0"
          marginWidth="0"
          allowFullScreen
          onLoad={() => setLoaded(true)}
          data-testid={`${testId}-iframe`}
          style={{ border: "none", display: "block" }}
        >
          Loading…
        </iframe>
      </div>

      <div className="text-center">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-accent hover:underline"
          data-testid={`${testId}-open-new-tab`}
        >
          <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
          {copy.openInTab}
        </a>
      </div>
    </div>
  );
}

export default MSFormEmbed;
